import {Injectable} from '@angular/core';
import {AuthConfig, OAuthService} from 'angular-oauth2-oidc';
import {JwksValidationHandler} from 'angular-oauth2-oidc-jwks';
import {HttpClient} from '@angular/common/http';
import {flatMap, map, tap} from 'rxjs/operators';
import {BehaviorSubject, Observable} from 'rxjs';
import {User} from '../models/user.model';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private static readonly userEvents: BehaviorSubject<User> = new BehaviorSubject<User>(undefined);

    constructor(private httpClient: HttpClient, private oauthService: OAuthService) {
    }

    public getUserEvents(): BehaviorSubject<User> {
        return AuthService.userEvents;
    }

    public configure(publicApiUrl: string, clientId: string): Observable<boolean> {
        return this
            .fetchConfig(publicApiUrl, clientId)
            .pipe(
                flatMap(config => {
                    this.oauthService.configure({...config, showDebugInformation: false});
                    this.oauthService.setupAutomaticSilentRefresh();
                    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
                    return this.oauthService.loadDiscoveryDocumentAndTryLogin();
                }),
                tap(_ => this.getUserEvents().next(this.getUser()))
            );
    }

    public getUser(): User {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const claims: any = this.oauthService.getIdentityClaims();
        if (!claims) {
            return null;
        }
        const user = new User();
        user.login = claims.preferred_username;
        user.roles = claims.realm_access.roles;
        user.firstName = claims.given_name;
        user.lastName = claims.family_name;
        return user;
    }

    public isLoggedIn(): boolean {
        return this.oauthService.hasValidAccessToken();
    }

    public login(): void {
        this.oauthService.initImplicitFlow();
    }

    public logout(): Observable<void> {
        this.oauthService.logOut();
        return new Observable<void>(observer => observer.next());
    }

    private fetchConfig(publicApiUrl: string, clientId: string): Observable<AuthConfig> {
        return this.httpClient
            .get<ProvidedAuthConfig>(`${publicApiUrl}/api/public/auth/config`)
            .pipe(map(config => this.buildConfig(config, clientId)));
    }

    private buildConfig(baseConfig: ProvidedAuthConfig, clientId: string): AuthConfig {
        return {
            redirectUri: `${window.location.href}`,
            postLogoutRedirectUri: `${window.location.origin}/index.html`,
            clientId,
            scope: 'openid profile email roles offline_access',
            oidc: true,
            issuer: baseConfig.issuer,
            requireHttps: baseConfig.issuer.indexOf('https') >= 0,
            silentRefreshRedirectUri: `${window.location.origin}/silent-refresh.html`,
            showDebugInformation: true,
            sessionChecksEnabled: true
        };
    }
}

interface ProvidedAuthConfig {
    issuer: string;
}
